import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import api from '../../api/api';
import { AuthContext } from '../auth-context/auth';
import './callsign-prompt.scss';
import { globalVariables } from '../../config';

const CallsignPrompt = ({ onClose }) => {
    const { t } = useTranslation();
    const { pilot, login } = useContext(AuthContext);
    const [callsign, setCallsign] = useState('');
    const [selectedHub, setSelectedHub] = useState('');
    const [hubs, setHubs] = useState([]);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [allowSelection, setAllowSelection] = useState(true);
    const [assignedCallsign, setAssignedCallsign] = useState('');
    const [availableCallsigns, setAvailableCallsigns] = useState([]);

    useEffect(() => {
        const fetchCallsignStatus = async () => {
            try {
                const response = await api.get('/auth/next-callsign');
                setAllowSelection(response.data.allowSelection);
                if (response.data.allowSelection) {
                    const callsigns = response.data.availableCallsigns.map((cs) =>
                        cs.replace(globalVariables.OACI, '')
                    );
                    setAvailableCallsigns(callsigns);
                } else {
                    setAssignedCallsign(response.data.nextCallsign);
                }
            } catch (err) {
                setError(t('fetch_callsign_error'));
                console.error('Error fetching callsign status:', err);
            }
        };
        
        const fetchHubs = async () => {
            try {
                const response = await api.get('/hubs');
                if (response.data && response.data.length > 0) {
                    setHubs(response.data);
                    // Auto-select if there's only one hub
                    if (response.data.length === 1) {
                        setSelectedHub(response.data[0].id);
                    }
                }
            } catch (err) {
                console.error('Error fetching hubs:', err);
            }
        };
        
        fetchCallsignStatus();
        fetchHubs();
    }, [t]);

    const handleSelectChange = (selectedOption) => {
        const newCallsign = selectedOption ? selectedOption.value : '';
        setCallsign(newCallsign);
        setError('');
    };

    const handleInputChange = (inputValue, { action }) => {
        if (action === 'input-change') {
            const value = inputValue.replace(/[^0-9]/g, '').slice(0, 3);
            setCallsign(value);
            setError('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsSubmitting(true);

        try {
            if (allowSelection) {
                if (!callsign) {
                    setError(t('callsign_required'));
                    setIsSubmitting(false);
                    return;
                }

                const callsignNumberRegex = /^[0-9]{3}$/;
                if (!callsignNumberRegex.test(callsign)) {
                    setError(t('callsign_invalid'));
                    setIsSubmitting(false);
                    return;
                }

                if (!availableCallsigns.includes(callsign)) {
                    setError(t('callsign_not_available'));
                    setIsSubmitting(false);
                    return;
                }

                const response = await api.post('/auth/update-callsign', { 
                    callsign,
                    hubId: selectedHub || null
                });
                
                // Update the entire pilot object including hub
                login(
                    response.data.pilot,
                    localStorage.getItem('token')
                );
            } else {
                const response = await api.post('/auth/update-callsign', {
                    hubId: selectedHub || null
                });
                
                // Update the entire pilot object including hub
                login(
                    response.data.pilot,
                    localStorage.getItem('token')
                );
            }

            setError('');
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || t('update_callsign_error'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const callsignOptions = availableCallsigns.map((cs) => ({
        value: cs,
        label: `${globalVariables.OACI}${cs}`,
    }));

    return (
        <div className="callsign-prompt-overlay">
            <div className="callsign-prompt">
                <h2>{allowSelection ? t('select_callsign') : t('confirm_callsign')}</h2>
                {allowSelection ? (
                    <>
                        <p>{t('choose_or_enter_callsign', { oaci: globalVariables.OACI })}</p>
                        <form onSubmit={handleSubmit}>
                            <Select
                                options={callsignOptions}
                                value={
                                    callsign
                                        ? { value: callsign, label: `${globalVariables.OACI}${callsign}` }
                                        : null
                                }
                                onChange={handleSelectChange}
                                onInputChange={handleInputChange}
                                placeholder={t('select_callsign')}
                                isClearable
                                className="callsign-select"
                                classNamePrefix="react-select"
                                formatOptionLabel={({ label }) => label}
                            />
                            
                            {/* Hub Selection */}
                            {hubs.length > 0 && (
                                <div className="hub-selection">
                                    <label>{t('select_base') || 'Select your base:'}</label>
                                    <select 
                                        value={selectedHub} 
                                        onChange={(e) => setSelectedHub(e.target.value)}
                                        className="hub-select"
                                        required
                                    >
                                        {hubs.length > 1 && <option value="">-- {t('select_base') || 'Select Base'} --</option>}
                                        {hubs.map(hub => (
                                            <option key={hub.id} value={hub.id}>
                                                {hub.airport.icao} - {hub.airport.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            
                            {error && <p className="error">{error}</p>}
                            <button type="submit" className="btn" disabled={isSubmitting || (hubs.length > 0 && !selectedHub)}>
                                {isSubmitting ? t('saving') : t('save_callsign')}
                            </button>
                        </form>
                    </>
                ) : (
                    <>
                        <p
                            dangerouslySetInnerHTML={{
                                __html: t('confirm_assigned_callsign', { callsign: assignedCallsign }),
                            }}
                        />
                        <form onSubmit={handleSubmit}>
                            {/* Hub Selection for automatic callsign */}
                            {hubs.length > 0 && (
                                <div className="hub-selection">
                                    <label>{t('select_base') || 'Select your base:'}</label>
                                    <select 
                                        value={selectedHub} 
                                        onChange={(e) => setSelectedHub(e.target.value)}
                                        className="hub-select"
                                        required
                                    >
                                        {hubs.length > 1 && <option value="">-- {t('select_base') || 'Select Base'} --</option>}
                                        {hubs.map(hub => (
                                            <option key={hub.id} value={hub.id}>
                                                {hub.airport.icao} - {hub.airport.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            
                            {error && <p className="error">{error}</p>}
                            <button type="submit" className="btn" disabled={isSubmitting || (hubs.length > 0 && !selectedHub)}>
                                {isSubmitting ? t('confirming') : t('confirm_callsign')}
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};

export default CallsignPrompt;